import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../_services/notification.service';
import { ConfirmDeleteDialogComponent } from '../gestion-usuarios/dialogs/confirm-delete-dialog.component';
import { GestionListadosService } from './gestion-listados.service';
import { Listado, ListadoOpcion } from './listado.model';

@Component({
  selector: 'app-gestion-listados',
  templateUrl: './gestion-listados.component.html',
  styleUrls: ['./gestion-listados.component.scss']
})
export class GestionListadosComponent implements OnInit {

  listados: Listado[] = [];
  selectedId: number | null = null;
  loading = false;

  filtroSearch = '';
  filtroSearchControl = '';
  filtroEstado: 'todos' | 'activos' | 'inactivos' = 'todos';

  showNewListadoForm = false;
  editingListado = false;
  listadoForm: FormGroup;

  showNewOpcionRow = false;
  editingOpcionId: number | null = null;
  opcionForm: FormGroup;

  private searchDebounce: any;

  constructor(
    private fb: FormBuilder,
    private service: GestionListadosService,
    private notif: NotificationService,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {
    this.listadoForm = this.buildListadoForm();
    this.opcionForm = this.buildOpcionForm();
  }

  ngOnInit(): void {
    this.cargaListados();
  }

  // ---------- Carga ----------
  cargaListados(): void {
    this.loading = true;
    this.service.list({
      page: 0,
      size: 100,
      search: this.filtroSearch,
      activo: this.estadoToBool()
    }).subscribe({
      next: (resp) => {
        this.listados = resp.data || [];
        if (this.selectedId == null && this.listados.length > 0) {
          this.selectedId = this.listados[0].id;
          this.cargaListadoCompleto(this.selectedId);
        } else if (this.selectedId != null) {
          this.cargaListadoCompleto(this.selectedId);
        }
        this.loading = false;
      },
      error: () => {
        this.notif.error(
          this.translate.instant('COMMON.ERROR'),
          this.translate.instant('LISTADOS.MSG.LOAD_FAIL'),
          3000
        );
        this.loading = false;
      }
    });
  }

  private cargaListadoCompleto(id: number): void {
    this.service.getById(id).subscribe({
      next: (full) => {
        const idx = this.listados.findIndex(l => l.id === id);
        if (idx >= 0) this.listados[idx] = full;
      },
      error: () => {}
    });
  }

  private estadoToBool(): boolean | undefined {
    if (this.filtroEstado === 'activos') return true;
    if (this.filtroEstado === 'inactivos') return false;
    return undefined;
  }

  // ---------- Selección ----------
  select(l: Listado): void {
    this.selectedId = l.id;
    this.editingListado = false;
    this.showNewOpcionRow = false;
    this.editingOpcionId = null;
    this.cargaListadoCompleto(l.id);
  }

  get selected(): Listado | undefined {
    return this.listados.find(l => l.id === this.selectedId);
  }

  // ---------- Filtros ----------
  onSearchChange(value: string): void {
    this.filtroSearchControl = value;
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.filtroSearch = value;
      this.cargaListados();
    }, 350);
  }

  onEstadoChange(value: 'todos' | 'activos' | 'inactivos'): void {
    this.filtroEstado = value;
    this.cargaListados();
  }

  // ---------- Listado CRUD ----------
  abrirNuevoListado(): void {
    this.showNewListadoForm = true;
    this.listadoForm = this.buildListadoForm();
  }

  cancelarNuevoListado(): void {
    this.showNewListadoForm = false;
    this.listadoForm.reset();
  }

  guardarNuevoListado(): void {
    if (!this.listadoForm.valid) return;
    const payload = this.normalizeListadoPayload(this.listadoForm.value);
    this.service.create(payload).subscribe({
      next: (resp: any) => {
        this.notif.success(
          this.translate.instant('COMMON.DONE'),
          this.translate.instant('LISTADOS.MSG.CREATED'),
          2800
        );
        this.showNewListadoForm = false;
        this.selectedId = resp?.listado?.id || null;
        this.cargaListados();
      },
      error: (err) => this.notifError(err)
    });
  }

  abrirEditarListado(): void {
    if (!this.selected) return;
    this.editingListado = true;
    this.listadoForm = this.buildListadoForm(this.selected);
  }

  cancelarEditarListado(): void {
    this.editingListado = false;
    this.listadoForm.reset();
  }

  guardarEdicionListado(): void {
    if (!this.selected || !this.listadoForm.valid) return;
    const payload = this.normalizeListadoPayload(this.listadoForm.value);
    this.service.update(this.selected.id, payload).subscribe({
      next: () => {
        this.notif.success(
          this.translate.instant('COMMON.DONE'),
          this.translate.instant('LISTADOS.MSG.UPDATED'),
          2800
        );
        this.editingListado = false;
        this.cargaListados();
      },
      error: (err) => this.notifError(err)
    });
  }

  confirmarEliminarListado(): void {
    if (!this.selected) return;
    const l = this.selected;
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      data: {
        title: this.translate.instant('LISTADOS.MSG.DELETE_TITLE'),
        message: this.translate.instant('LISTADOS.MSG.DELETE_MSG', { nombre: l.nombre }),
        confirmLabel: this.translate.instant('COMMON.DELETE')
      },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((ok) => {
      if (!ok) return;
      this.service.delete(l.id).subscribe({
        next: () => {
          this.notif.success(
            this.translate.instant('COMMON.DONE'),
            this.translate.instant('LISTADOS.MSG.DELETED'),
            2800
          );
          this.selectedId = null;
          this.cargaListados();
        },
        error: (err) => this.notifError(err)
      });
    });
  }

  // ---------- Opciones ----------
  abrirNuevaOpcion(): void {
    if (!this.selected) return;
    const ordenSugerido = (this.selected.opciones?.length || 0) + 1;
    this.showNewOpcionRow = true;
    this.editingOpcionId = null;
    this.opcionForm = this.buildOpcionForm({ orden: ordenSugerido } as ListadoOpcion);
  }

  cancelarNuevaOpcion(): void {
    this.showNewOpcionRow = false;
    this.opcionForm.reset();
  }

  guardarNuevaOpcion(): void {
    if (!this.selected || !this.opcionForm.valid) return;
    this.service.addOpcion(this.selected.id, this.opcionForm.value).subscribe({
      next: () => {
        this.notif.success(
          this.translate.instant('COMMON.DONE'),
          this.translate.instant('LISTADOS.MSG.OPTION_ADDED'),
          2500
        );
        this.showNewOpcionRow = false;
        this.cargaListadoCompleto(this.selected!.id);
      },
      error: (err) => this.notifError(err)
    });
  }

  abrirEditarOpcion(op: ListadoOpcion): void {
    this.editingOpcionId = op.id;
    this.showNewOpcionRow = false;
    this.opcionForm = this.buildOpcionForm(op);
  }

  cancelarEditarOpcion(): void {
    this.editingOpcionId = null;
    this.opcionForm.reset();
  }

  guardarEdicionOpcion(): void {
    if (this.editingOpcionId == null || !this.opcionForm.valid) return;
    this.service.updateOpcion(this.editingOpcionId, this.opcionForm.value).subscribe({
      next: () => {
        this.notif.success(
          this.translate.instant('COMMON.DONE'),
          this.translate.instant('LISTADOS.MSG.OPTION_UPDATED'),
          2500
        );
        this.editingOpcionId = null;
        if (this.selected) this.cargaListadoCompleto(this.selected.id);
      },
      error: (err) => this.notifError(err)
    });
  }

  toggleActivoOpcion(op: ListadoOpcion): void {
    this.service.toggleOpcion(op.id).subscribe({
      next: () => {
        op.activo = !op.activo;
      },
      error: (err) => this.notifError(err)
    });
  }

  moverOpcion(op: ListadoOpcion, direction: 'up' | 'down'): void {
    this.service.moveOpcion(op.id, direction).subscribe({
      next: () => { if (this.selected) this.cargaListadoCompleto(this.selected.id); },
      error: (err) => this.notifError(err)
    });
  }

  confirmarEliminarOpcion(op: ListadoOpcion): void {
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      data: {
        title: this.translate.instant('LISTADOS.MSG.DELETE_OPTION_TITLE'),
        message: this.translate.instant('LISTADOS.MSG.DELETE_OPTION_MSG', { etiqueta: op.etiqueta }),
        confirmLabel: this.translate.instant('COMMON.DELETE')
      },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((ok) => {
      if (!ok) return;
      this.service.deleteOpcion(op.id).subscribe({
        next: () => {
          this.notif.success(
            this.translate.instant('COMMON.DONE'),
            this.translate.instant('LISTADOS.MSG.OPTION_DELETED'),
            2500
          );
          if (this.selected) this.cargaListadoCompleto(this.selected.id);
        },
        error: (err) => this.notifError(err)
      });
    });
  }

  // ---------- Helpers ----------
  onCodigoChange(value: string): void {
    const upper = (value || '').toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    this.listadoForm.get('codigo')?.setValue(upper, { emitEvent: false });
  }

  countActivas(l: Listado): number { return (l.opciones || []).filter(o => o.activo).length; }
  countTotal(l: Listado): number { return (l.opciones || []).length; }

  opcionesOrdenadas(l: Listado | undefined): ListadoOpcion[] {
    if (!l) return [];
    return [...(l.opciones || [])].sort((a, b) => a.orden - b.orden);
  }

  primeraOpcion(l: Listado, op: ListadoOpcion): boolean {
    const arr = this.opcionesOrdenadas(l);
    return arr.length > 0 && arr[0].id === op.id;
  }
  ultimaOpcion(l: Listado, op: ListadoOpcion): boolean {
    const arr = this.opcionesOrdenadas(l);
    return arr.length > 0 && arr[arr.length - 1].id === op.id;
  }

  private buildListadoForm(initial?: Listado): FormGroup {
    return this.fb.group({
      codigo:      [initial?.codigo || '', [Validators.required, Validators.pattern(/^[A-Z0-9_]+$/)]],
      nombre:      [initial?.nombre || '', Validators.required],
      descripcion: [initial?.descripcion || ''],
      orden:       [initial?.orden ?? 1, [Validators.required, Validators.min(1)]],
      activo:      [initial?.activo ?? true]
    });
  }

  private buildOpcionForm(initial?: Partial<ListadoOpcion>): FormGroup {
    return this.fb.group({
      orden:    [initial?.orden ?? 1, [Validators.required, Validators.min(1)]],
      valor:    [initial?.valor || '', Validators.required],
      etiqueta: [initial?.etiqueta || '', Validators.required],
      activo:   [initial?.activo ?? true]
    });
  }

  private normalizeListadoPayload(v: any): any {
    return {
      codigo: (v.codigo || '').toUpperCase(),
      nombre: v.nombre,
      descripcion: v.descripcion || null,
      orden: Number(v.orden) || 1,
      activo: !!v.activo
    };
  }

  private notifError(err: any): void {
    const msg = err?.error?.message || this.translate.instant('COMMON.ERROR');
    this.notif.error(this.translate.instant('COMMON.ERROR'), msg, 3500);
  }
}
